import React, { useState } from 'react';
import ImagePopup from './ImagePopup';

/**
 * Interface for a news link
 */
interface NewsLink {
  title: string;
  url: string;
  highlight: boolean;
  imageSrc?: string; // Optional thumbnail image source
  fullImageSrc?: string; // Optional full-size image source
  comment?: string; // Optional commentary text
}

/**
 * Props for the NewsColumn component
 */
interface NewsColumnProps {
  categoryName: string;
  links: NewsLink[];
}

/**
 * NewsColumn component that displays a category of news links
 */
const NewsColumn: React.FC<NewsColumnProps> = ({ categoryName, links }) => {
  // Track which images failed to load
  const [failedImages, setFailedImages] = useState<{[key: number]: boolean}>({});
  
  // Handler for image load errors
  const handleImageError = (index: number) => {
    setFailedImages(prev => ({...prev, [index]: true}));
  };

  return (
    <div className="news-column">
      <h2 className="category-heading">{categoryName}</h2>
      <ul className="news-list">
        {links.map((link, index) => (
          <li key={index} className={link.highlight ? 'highlight' : ''}>
            <div className="news-item">
              <div className="news-text">
                <a 
                  href={link.url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  {link.title}
                </a>
                {link.comment && (
                  <p className="news-comment">{link.comment}</p>
                )}
              </div>
              
              {/* Show ImagePopup only if image source exists and hasn't failed to load */}
              {link.imageSrc && link.imageSrc.trim() !== '' && !failedImages[index] && (
                <div onError={() => handleImageError(index)}>
                  <ImagePopup 
                    thumbnailSrc={link.imageSrc}
                    fullSizeSrc={link.fullImageSrc || link.imageSrc}
                    altText={link.title}
                  />
                </div>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default NewsColumn; 